import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Badge } from '../../../components/ui/Badge';
import { getCountryName } from '../../../utils/countries';
import {
  formatSupplierCategory,
  formatSupplierType,
  formatMsmeStatus,
  formatMsmeCategory
} from '../../../utils/formatters';

const VendorTable = ({ 
  vendors, 
  selectedVendors, 
  onVendorSelect, 
  onSelectAll, 
  onSort, 
  sortConfig,
  onViewVendor,
  onEditVendor,
  onExportVendor,
  onAssignVendorCode
}) => {
  const navigate = useNavigate();
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRowExpansion = (vendorId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(vendorId)) {
      newExpanded.delete(vendorId);
    } else {
      newExpanded.add(vendorId);
    }
    setExpandedRows(newExpanded);
  };

  const handleVendorClick = (vendor, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!vendor?.id) {
      console.error('Vendor ID is missing:', vendor);
      return;
    }
    navigate(`/vendor-profile-details/${vendor.id}`, { state: { vendor } });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      ACTIVE: { variant: 'success', label: 'Active' },
      PENDING: { variant: 'warning', label: 'Pending' },
      UNDER_REVIEW: { variant: 'info', label: 'Under Review' },
      APPROVED: { variant: 'success', label: 'Approved' },
      REJECTED: { variant: 'destructive', label: 'Rejected' },
      INACTIVE: { variant: 'secondary', label: 'Inactive' },
      SUSPENDED: { variant: 'destructive', label: 'Suspended' },
      // Also support lowercase for backwards compatibility
      active: { variant: 'success', label: 'Active' },
      pending: { variant: 'warning', label: 'Pending' },
      under_review: { variant: 'info', label: 'Under Review' },
      approved: { variant: 'success', label: 'Approved' },
      rejected: { variant: 'destructive', label: 'Rejected' },
      inactive: { variant: 'secondary', label: 'Inactive' }
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    return (
      <Badge variant={config.variant} className="text-xs">
        {config.label}
      </Badge>
    );
  };

  const getApprovalStageBadge = (stage) => {
    const stageConfig = {
      'level_1': { variant: 'outline', label: 'First approval' },
      'level_2': { variant: 'default', label: 'Level 2' },
      'approved': { variant: 'success', label: 'Approved' },
      'rejected': { variant: 'destructive', label: 'Rejected' }
    };

    const config = stageConfig[stage] || stageConfig.level_1;
    return (
      <Badge variant={config.variant} className="text-xs">
        {config.label}
      </Badge>
    );
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) {
      return <Icon name="ArrowUpDown" size={14} className="text-slate-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <Icon name="ArrowUp" size={14} className="text-slate-700" />
      : <Icon name="ArrowDown" size={14} className="text-slate-700" />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Human-friendly document type labels
  const formatDocumentType = (docType) => {
    if (!docType) return 'Document';
    const map = {
      gst_certificate: 'GST Certificate',
      pan_card: 'PAN Card',
      bank_statement: 'Bank Statement',
      msme_certificate: 'MSME Certificate',
      msme_declaration: 'MSME Declaration',
      company_registration: 'Company Registration',
      incorporation_certificate: 'Incorporation Certificate',
      business_license: 'Business License',
      insurance_certificate: 'Insurance Certificate',
      quality_certificate: 'Quality Certificate',
      tax_certificate: 'Tax Certificate',
      other: 'Other Document'
    };
    if (map[docType]) return map[docType];
    // Fallback: titleize with spaces
    return String(docType)
      .split('_')
      .map(w => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
      .join(' ');
  };

  return (
    <div className="overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="w-12 px-4 py-3">
                <Checkbox
                  checked={selectedVendors.length === vendors.length && vendors.length > 0}
                  onChange={(e) => onSelectAll(e.target.checked)}
                />
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('vendor_code')}
                  className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-slate-900 transition-colors"
                >
                  <span>Vendor Code</span>
                  {getSortIcon('vendor_code')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('company_name')}
                  className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-slate-900 transition-colors"
                >
                  <span>Company Name</span>
                  {getSortIcon('company_name')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('msme_category')}
                  className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-slate-900 transition-colors"
                >
                  <span>Category</span>
                  {getSortIcon('msme_category')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('country_origin')}
                  className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-slate-900 transition-colors"
                >
                  <span>Country</span>
                  {getSortIcon('country_origin')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('status')}
                  className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-slate-900 transition-colors"
                >
                  <span>Status</span>
                  {getSortIcon('status')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => onSort('created_at')}
                  className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-slate-900 transition-colors"
                >
                  <span>Registration Date</span>
                  {getSortIcon('created_at')}
                </button>
              </th>
              <th className="px-4 py-3 text-center">
                <span className="text-sm font-semibold text-slate-700">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {vendors.map((vendor) => (
              <React.Fragment key={vendor.id}>
                <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <Checkbox
                      checked={selectedVendors.includes(vendor.id)}
                      onChange={(e) => onVendorSelect(vendor.id, e.target.checked)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => handleVendorClick(vendor, e)}
                        className="font-mono text-sm font-medium text-slate-900 hover:text-primary hover:underline"
                        title="View vendor profile"
                      >
                        {vendor.vendor_code || 'Pending'}
                      </button>
                      <button
                        onClick={(e) => handleVendorClick(vendor, e)}
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                        title="View vendor profile"
                      >
                        <Icon 
                          name="ChevronRight" 
                          size={16} 
                        />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <div 
                        className="font-medium text-slate-900 hover:text-primary cursor-pointer transition-colors"
                        onClick={(e) => handleVendorClick(vendor, e)}
                      >
                        {vendor.company_name}
                      </div>
                      <div className="text-sm text-slate-600">{vendor.contact_person_name}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-900">{formatMsmeCategory(vendor.msme_status, vendor.msme_category)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-slate-900">{getCountryName(vendor.country_origin)}</span>
                      {vendor.msme_status === 'msme' && (
                        <Badge variant="outline" className="text-xs">
                          MSME
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(vendor.status)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-900">
                      {formatDate(vendor.created_at)}
                    </span>
                  </td>
                  {/* <td className="px-4 py-3">
                    {getApprovalStageBadge(vendor.approvalStage)}
                  </td> */}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center space-x-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onViewVendor(vendor)}
                        className="h-8 w-8"
                      >
                        <Icon name="Eye" size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEditVendor(vendor)}
                        className="h-8 w-8"
                      >
                        <Icon name="Edit" size={14} />
                      </Button>
                      {(() => {
                        const finalApproval = vendor.approvals?.find(approval => approval.level === 'FINAL');
                        const level2Approval = vendor.approvals?.find(approval => approval.level === 'LEVEL_2');
                        const acknowledgementOk = !vendor.acknowledgement ||
                          vendor.acknowledgement.status === 'APPROVED' ||
                          vendor.acknowledgement.status === 'RESOLVED';
                        const canAssignCode = !vendor.vendor_code &&
                                             acknowledgementOk &&
                                             (finalApproval?.status === 'APPROVED' || level2Approval?.status === 'APPROVED');
                        
                        const tooltipMessage = !vendor.vendor_code 
                          ? (!acknowledgementOk
                              ? (vendor.acknowledgement?.status === 'PENDING'
                                  ? 'Waiting for vendor acknowledgement'
                                  : vendor.acknowledgement?.status === 'CONFLICTED'
                                    ? 'Resolve acknowledgement conflict first'
                                    : 'Complete acknowledgement first')
                              : (finalApproval?.status === 'APPROVED' || level2Approval?.status === 'APPROVED')
                                ? 'Assign vendor code'
                                : 'Complete Level 2 approval first')
                          : 'Code already assigned';

                        return canAssignCode && onAssignVendorCode ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onAssignVendorCode(vendor)}
                            className="h-8 w-8"
                            title={tooltipMessage}
                          >
                            <Icon name="Hash" size={14} />
                          </Button>
                        ) : null;
                      })()}
                      {/* <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onExportVendor(vendor)}
                        className="h-8 w-8"
                      >
                        <Icon name="Download" size={14} />
                      </Button> */}
                    </div>
                  </td>
                </tr>
                
                {/* Expanded Row Details */}
                {expandedRows.has(vendor.id) && (
                  <tr className="bg-slate-50">
                    <td colSpan="9" className="px-4 py-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <h4 className="font-medium text-slate-900 mb-2">Contact Information</h4>
                          <div className="space-y-1 text-sm text-slate-600">
                            <div>Email: {vendor.email}</div>
                            <div>Phone: {vendor.phone_number}</div>
                            <div>Address: {vendor.registered_address}</div>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-900 mb-2">Business Details</h4>
                          <div className="space-y-1 text-sm text-slate-600">
                            <div>Type: {formatSupplierType(vendor.supplier_type)}</div>
                            <div>Category: {formatSupplierCategory(vendor.supplier_category)}</div>
                            <div>MSME Status: {formatMsmeStatus(vendor.msme_status)}</div>
                            <div>Turnover: {formatCurrency(vendor.annual_turnover || 0)}</div>
                            <div>GST: {vendor.gst_number || 'N/A'}</div>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-900 mb-2">Documents</h4>
                          <div className="flex flex-wrap gap-2">
                            {vendor.documents.map((doc, index) => (
                              <Badge
                                key={index}
                                variant="outline"
                                className="text-xs"
                              >
                                {typeof doc === 'object' ? formatDocumentType(doc.document_type) : formatDocumentType(doc)}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden">
        {vendors.map((vendor) => (
          <div key={vendor.id} className="border-b border-slate-200 p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start space-x-3">
                <Checkbox
                  checked={selectedVendors.includes(vendor.id)}
                  onChange={(e) => onVendorSelect(vendor.id, e.target.checked)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div 
                    className="font-medium text-slate-900 hover:text-primary cursor-pointer transition-colors"
                    onClick={(e) => handleVendorClick(vendor, e)}
                  >
                    {vendor.company_name}
                  </div>
                  <div className="text-sm text-slate-600">{vendor.contact_person_name}</div>
                  <button
                    onClick={(e) => handleVendorClick(vendor, e)}
                    className="font-mono text-sm text-primary mt-1 hover:underline text-left"
                    title="View vendor profile"
                  >
                    {vendor.vendor_code || 'Pending'}
                  </button>
                </div>
              </div>
              <button
                onClick={(e) => handleVendorClick(vendor, e)}
                className="text-slate-400 hover:text-slate-600 transition-colors p-1"
                title="View vendor profile"
              >
                <Icon 
                  name="ChevronRight" 
                  size={20} 
                />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <div className="text-xs text-slate-500 mb-1">Category</div>
                <div className="text-sm text-slate-900">{formatMsmeCategory(vendor.msme_status, vendor.msme_category)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Country</div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-slate-900">{getCountryName(vendor.country_origin)}</span>
                  {vendor.msme_status === 'msme' && (
                    <Badge variant="outline" className="text-xs">
                      MSME
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {getStatusBadge(vendor.status)}
                {vendor.approval_stage && getApprovalStageBadge(vendor.approval_stage)}
              </div>
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onViewVendor(vendor)}
                  className="h-8 w-8"
                >
                  <Icon name="Eye" size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEditVendor(vendor)}
                  className="h-8 w-8"
                >
                  <Icon name="Edit" size={14} />
                </Button>
                {(() => {
                  const finalApproval = vendor.approvals?.find(approval => approval.level === 'FINAL');
                  const level2Approval = vendor.approvals?.find(approval => approval.level === 'LEVEL_2');
                  const acknowledgementOk = !vendor.acknowledgement ||
                    vendor.acknowledgement.status === 'APPROVED' ||
                    vendor.acknowledgement.status === 'RESOLVED';
                  const canAssignCode = !vendor.vendor_code &&
                    acknowledgementOk &&
                    (finalApproval?.status === 'APPROVED' || level2Approval?.status === 'APPROVED');
                  
                  const tooltipMessage = !vendor.vendor_code 
                    ? (!acknowledgementOk
                        ? (vendor.acknowledgement?.status === 'PENDING'
                            ? 'Waiting for vendor acknowledgement'
                            : vendor.acknowledgement?.status === 'CONFLICTED'
                              ? 'Resolve acknowledgement conflict first'
                              : 'Complete acknowledgement first')
                        : (finalApproval?.status === 'APPROVED' || level2Approval?.status === 'APPROVED')
                          ? 'Assign vendor code'
                          : 'Complete Level 2 approval first')
                    : 'Code already assigned';

                  return canAssignCode && onAssignVendorCode ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onAssignVendorCode(vendor)}
                      className="h-8 w-8"
                      title={tooltipMessage}
                    >
                      <Icon name="Hash" size={14} />
                    </Button>
                  ) : null;
                })()}
              </div>
            </div>

            {/* Mobile Expanded Details */}
            {expandedRows.has(vendor.id) && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-slate-900 mb-2">Contact Information</h4>
                    <div className="space-y-1 text-sm text-slate-600">
                      <div>Email: {vendor.email}</div>
                      <div>Phone: {vendor.phone_number}</div>
                      <div>Address: {vendor.registered_address}</div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900 mb-2">Business Details</h4>
                    <div className="space-y-1 text-sm text-slate-600">
                      <div>Type: {vendor.supplier_type}</div>
                      <div>Turnover: {formatCurrency(vendor.annual_turnover || 0)}</div>
                      <div>Registration: {formatDate(vendor.created_at)}</div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900 mb-2">Documents</h4>
                    <div className="flex flex-wrap gap-2">
                      {vendor.documents.map((doc, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="text-xs"
                        >
                          {typeof doc === 'object' ? formatDocumentType(doc.document_type) : formatDocumentType(doc)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default VendorTable;